import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';

import type { GridColDef } from '@p81-common/ui';
import { showNotif } from '@p81-common/ui';

import { buildEdgesListParams, buildUrl } from './helpers';
import {
  checkingForExistenceOfOptions,
  clearOrDefaultFilters,
  parsConfigForHistory,
  replaceHistoryCML,
  reParsConfigForHistory
} from 'utils/common';
import { getRequest } from 'utils/requests';
import { useDataGrid } from 'hooks/useDataGrid';
import {
  EDGES_LIST_URL,
  EDGES_FILTERS_URL,
  defaultFilters,
  defaultNavigation,
  EdgeFilters,
  EdgeFiltersResponse
} from './configurates';

const REQUEST_DELAY = 200;

export const useEdgeDataGrid = (tableConfiguration: GridColDef[]) => {
  const history = useHistory();
  const { t } = useTranslation('edges');
  const isInitialized = useRef(false);
  const unmount = useRef(false);
  const [failedDrs, setFailedDrs] = useState<string[]>([]);

  const useDataGridResultProps = useDataGrid(
    tableConfiguration,
    defaultFilters
  );

  const {
    setFilter,
    filter,
    setResults,
    setTableData,
    tableConfigDisplay,
    setLoaderTable,
    direction,
    startFilter,
    setStartFilter,
    setVisibleFilterSave,
    setVisibleFilterEdit,
    changeFilter,
    setChangeFilter,
    trigerResetDatePicker,
    setTrigerResetDatePicker,
    tableConf,
    setTableConf,
    filters,
    setFilters,
    setLoader,
    setDataConection,
    setSelectLoader,
    setPrevStateFilter,
    setPrevStateTableConf
  } = useDataGridResultProps;

  useEffect(() => {
    if (!isInitialized.current) return;

    replaceHistoryCML(
      direction,
      filter,
      undefined,
      history,
      false,
      false,
      changeFilter,
      parsConfigForHistory(tableConf)
    );

    // eslint-disable-next-line
  }, [startFilter, tableConf]);

  useEffect(() => {
    setLoaderTable(true);
    const query = new URLSearchParams(history.location.search);
    const queryFilter = query.get('filter') || '';
    const queryTableConf = query.get('tableConf') || '';
    if (!queryFilter) {
      const isDefaultFilter = isEqual(filter, {
        ...defaultFilters,
        ...defaultNavigation
      });
      if (isDefaultFilter) {
        replaceHistoryCML(
          false,
          filter,
          undefined,
          history,
          'replace',
          false,
          'Default',
          parsConfigForHistory(tableConf)
        );
        setVisibleFilterEdit(false);
        setVisibleFilterSave(false);
        setPrevStateFilter(filter);
        setPrevStateTableConf(tableConf);
        delayedRequestForFilteredData();
      } else {
        resetAllfilters(true);
        replaceHistoryCML(
          false,
          filter,
          undefined,
          history,
          'replace',
          {
            ...defaultFilters,
            ...defaultNavigation
          },
          'Default',
          tableConf
        );
        setVisibleFilterEdit(false);
        setVisibleFilterSave(false);
      }
    } else {
      let parsedFilter;
      try {
        parsedFilter = JSON.parse(queryFilter);
      } catch (err) {
        if (!unmount.current) console.log(err);
      }
      setFilter(parsedFilter);
      delayedRequestForFilteredData(parsedFilter);

      if (queryTableConf) {
        setPrevStateTableConf(
          reParsConfigForHistory(JSON.parse(queryTableConf))
        );
        setTableConf(
          reParsConfigForHistory(JSON.parse(queryTableConf), tableConfigDisplay)
        );
      } else {
        setPrevStateTableConf(tableConf);
        setTableConf(tableConfigDisplay);
      }
    }
    setTrigerResetDatePicker(!trigerResetDatePicker);
    isInitialized.current = true;

    // eslint-disable-next-line
  }, [history.location.search]);

  useEffect(() => {
    return () => {
      unmount.current = true;
    };
  }, []);

  const getFilterOptionsFromBackend = (listFilter: typeof filter) => {
    setSelectLoader(true);
    getRequest(EDGES_FILTERS_URL)
      .then(res => {
        if (!unmount.current) {
          const { body } = res;
          setFilters({
            ...filters,
            data_residency: body?.data_residencies,
            region_name: body?.region_names,
            region_provider: body?.region_providers,
            machine_type: body?.machine_types,
            machine_size: body?.machine_sizes,
            edge_version: body?.edge_versions,
            status: body?.statuses
          });

          const newCheckingList = checkingForExistenceOfOptions(
            listFilter,
            setFilter,
            body
          );
          if (newCheckingList) {
            setPrevStateFilter(newCheckingList);
            replaceHistoryCML(
              '',
              filter,
              undefined,
              history,
              'validation',
              newCheckingList,
              changeFilter,
              parsConfigForHistory(tableConf)
            );
          }
          setSelectLoader(false);
        }
      })
      .catch(err => {
        if (!unmount.current) {
          console.log(err);
          showNotif(t('ERRORS.LOAD_FILTERS_FAILED'), {
            alertProps: { severity: 'error' }
          });
        }
      });
  };

  type EdgeFilterType = typeof filter & EdgeFilters;

  const requestForFilteredData = (listFilter = filter as EdgeFilterType) => {
    const queryParams = buildEdgesListParams(listFilter);
    const url = buildUrl(EDGES_LIST_URL, queryParams);

    getRequest(url)
      .then(res => {
        if (!unmount.current) {
          const { body } = res;
          setTableData(body?.data || []);
          setResults({
            ...body,
            totalRecords: body?.pagination?.total_items || 0,
            totalPages: body?.pagination?.total_pages || 0,
            page: body?.pagination?.page || 0
          });
          setDataConection(true);
          setLoader(false);
          setLoaderTable(false);

          if (body?.query_metadata?.failed_drs) {
            setFailedDrs(body.query_metadata.failed_drs);
          } else {
            setFailedDrs([]);
          }

          getFilterOptionsFromBackend(listFilter);
        }
      })
      .catch(err => {
        if (!unmount.current) {
          setDataConection(false);
          setLoader(false);
          setLoaderTable(false);
          console.log(err);
          showNotif(t('ERRORS.LOAD_EDGES_FAILED'), {
            alertProps: { severity: 'error' }
          });
        }
      });
  };

  const latestRequestRef = useRef(requestForFilteredData);
  latestRequestRef.current = requestForFilteredData;

  const delayedRequestForFilteredData = useRef(
    debounce(
      (listFilter?: EdgeFilterType) => latestRequestRef.current(listFilter),
      REQUEST_DELAY
    )
  ).current;

  const resetAllfilters = (
    reload?: boolean,
    onlyFilterLabel?: boolean,
    onClickDefault?: boolean,
    modeClearAll?: boolean
  ) => {
    const newValueFilters = clearOrDefaultFilters(modeClearAll, defaultFilters);

    setFilter({ ...newValueFilters, ...defaultNavigation });

    if ((changeFilter === 'Default' || onClickDefault) && !modeClearAll) {
      setVisibleFilterSave(false);
      setVisibleFilterEdit(false);
      setTableConf(JSON.parse(JSON.stringify(tableConfigDisplay)));
    } else {
      setVisibleFilterSave('button');
      if (changeFilter !== 'Default') {
        setVisibleFilterEdit(true);
      }
    }
    if (!onlyFilterLabel) {
      setChangeFilter('Default');
      setTableConf(JSON.parse(JSON.stringify(tableConfigDisplay)));
    }

    setTrigerResetDatePicker(!trigerResetDatePicker);
    if (!reload) {
      setStartFilter(!startFilter);
    }
  };

  return {
    ...useDataGridResultProps,
    filter: filter as EdgeFilterType,
    filters: filters as EdgeFilterType & EdgeFiltersResponse,
    failedDrs,
    resetAllfilters
  };
};
